import React, { useMemo, useEffect, useImperativeHandle, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Modal, Tabs, Spin } from 'antd';
import AcoreConfigProvider from '@/components/acore-config-provider';
import classnames from 'classnames';
import Card from '@/components/card';
import useRequest from '@/hooks/useRequest';
import { categoryInventoryList as apiCategoryInventoryList } from '@/apis/goodsPool/categoryInventoryList';
import {
  queryInveNeedConfirmRule,
  InveNeedConfirmRule,
  ChangedType,
  TypeRuleDetails,
  TypeRules,
} from '@/apis/goodsPool/queryInveNeedConfirmRule';
import StatusError from '@/components/status-error';
import Loadable from '@/components/loadable';
import LOAD_STATUS from '@/constants/load-status';
import JsLoading from '@/components/js-loading';
import moment from 'moment';
import Container from '@/components/container';
import { RuleTagRender } from '../category-inventory-table/components/render-HP-rule';
import './index.less';

function renderTab(timestamp: number, index: number) {
  const title = index === 0 ? '原始规则' : `更新记录${index}`;
  const desc = index === 0 ? '接收于' : '调整于';
  return (
    <div style={{ width: '120px' }}>
      <div className="title-of-history-tab">{title}</div>
      <div className="desc-of-history-tab">
        {desc}：{moment(timestamp).format('YYYY-MM-DD')}
      </div>
    </div>
  );
}

// 货盘更新记录
function InventoryUpdateHistory({ inventoryId, activityId }: { inventoryId: string; activityId: string }) {
  // 根据货盘查询货盘规则变更记录
  const { data, loading, run } = useRequest<InveNeedConfirmRule>(queryInveNeedConfirmRule, {
    manual: true,
  });

  useEffect(() => {
    run({ inventoryId, activityId });
  }, [inventoryId, activityId, run]);

  const newData = useMemo(() => {
    if (!data) return [];
    return [data.originalRule, ...(data.updateRules || [])]
      .filter((item) => !!item)
      .map((item) => {
        let { id } = item;
        // originalRule没有id属性
        id = id == null ? Math.random() : id;
        return { ...item, id: String(id) };
      });
  }, [data]);

  const [selectedIndex, setSelectedIndex] = useState('');

  useEffect(() => {
    if (newData.length === 0) return;

    // 选中最新的变更记录
    setSelectedIndex(newData[newData.length - 1].id);
  }, [newData]);

  const subStrategyOfSelectedHistory = useMemo(() => {
    if (!selectedIndex) return;
    return (
      newData.find((item: any) => {
        return item.id === selectedIndex;
      }) || ({} as any)
    );
  }, [selectedIndex, newData]);

  return (
    // @ts-ignore
    <Spin wrapperClassName="spin-min-h" spinning={loading}>
      {/* 货盘更新记录列表 */}
      <Tabs
        className="historytab-of-rule"
        size="small"
        type="card"
        activeKey={selectedIndex}
        onChange={setSelectedIndex}
      >
        {newData.map((item: any, index: number) => (
          <Tabs.TabPane tab={renderTab(item.operatedTime || item.updateTime, index)} key={item.id} />
        ))}
      </Tabs>
      {/* 每个更新记录变更及的所有子策略 */}
      {subStrategyOfSelectedHistory && (
        <SubStrategiesOfHistory
          // 因为一次操作历史只会操作一个子策略，所以子策略的增加还是删除operateType放在了历史记录数据里
          strategyOperateType={subStrategyOfSelectedHistory?.operateType}
          subStrategies={subStrategyOfSelectedHistory?.subStrategies}
        />
      )}
    </Spin>
  );
}

function TagUpdate({ type }: { type?: 1 | 2 | 3 }) {
  if (type === 1) {
    return (
      <span className="acore-success-color">
        <span className="acore-dot acore-dot-success" />
        新增
      </span>
    );
  } else if (type === 3) {
    return (
      <span className="acore-error-color">
        <span className="acore-dot acore-dot-error" />
        已删除
      </span>
    );
  }

  return null;
}

// function SubStrategyTabTitle({ subStrategyName, operateType }: { subStrategyName: string; operateType?: 1 | 2 | 3 }) {
//   return <span>{subStrategyName}</span>;
//   // 1 新增 2 修改 3 删除
//   // return (
//   //   <>
//   //     {subStrategyName} <TagUpdate type={operateType} />
//   //   </>
//   // );
// }

// 子策略列表
function SubStrategiesOfHistory({
  subStrategies = [],
  strategyOperateType,
}: {
  subStrategies: InveNeedConfirmRule['originalRule']['subStrategies'];
  strategyOperateType?: 1 | 2 | 3;
}) {
  const inventoryRules = subStrategies;
  const [curTab, selectedTab] = useState(inventoryRules?.[0]?.subStrategyId);

  useEffect(() => {
    if (!inventoryRules || inventoryRules.length === 0) return;
    selectedTab(inventoryRules[0].subStrategyId);
  }, [inventoryRules]);

  const rules = useMemo(() => {
    const a1 = inventoryRules.find((item: any) => item.subStrategyId === curTab) || { rules: undefined };
    return a1.rules || [];
  }, [inventoryRules, curTab]);

  if (!inventoryRules || inventoryRules.length === 0) return null;

  return (
    <>
      {/* 子策略列表 */}
      <Tabs activeKey={curTab} onChange={selectedTab}>
        {inventoryRules.map((item: any) => {
          return <Tabs.TabPane tab={item.subStrategyName} key={item.subStrategyId} />;
        })}
      </Tabs>
      {rules.map((item: any) => (
        <RuleItem key={item.ruleId} item={item} />
      ))}
    </>
  );
}

function RuleItem({ item }: { item: TypeRules }) {
  return (
    <Container
      margin={{ horizontal: false, top: false, bottom: true }}
      padding
      paddingSize="min"
      style={{ background: '#F3F4F5' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', marginBottom: '7px' }}>
        <span>{item.ruleName}</span>
        <TagUpdate type={item.operateType} />
      </div>
      {(Object.keys(mapChangeText) as ChangedType[]).map((key) => {
        if (!item[key]) return null;
        // 没有变更规则，当前项不展示
        if (item[key]!.length === 0) return null;

        return <UpdateTypeOfRule ruleDetails={item[key]!} changeType={key} key={key} />;
      })}
    </Container>
  );
}

// 规则变动类型
const mapChangeText: Record<ChangedType, { text?: string; icon?: string }> = {
  // 原始规则
  ruleDetails: { text: undefined, icon: undefined },
  addRuleDetails: {
    text: '新增的标签',
    icon: '#17C574',
  },
  updateRuleDetails: {
    text: '有调整的标签',
    icon: '#FEB00E',
  },
  deleteRuleDetails: {
    text: '删除的标签',
    icon: '#FF4C4C',
  },
};

// 一项规则由很多标签组成
function UpdateTypeOfRule({ changeType, ruleDetails }: { changeType: ChangedType; ruleDetails: TypeRuleDetails[] }) {
  return (
    <div style={{ marginBottom: '8px' }}>
      {mapChangeText[changeType].text && (
        <div style={{ lineHeight: '20px' }}>
          <span
            style={{
              marginRight: '5px',
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              display: 'inline-block',
              backgroundColor: mapChangeText[changeType].icon,
            }}
          ></span>
          {mapChangeText[changeType].text}
        </div>
      )}
      <RuleTagRender tagStyle1 rules={ruleDetails} />
    </div>
  );
}

//-------以上渲染子策略----------------------

function FullStrategyItem({ inventoryName = '', inventoryId = '', creatorErp = '' }) {
  return (
    <Container
      margin={false}
      style={{
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#F3F4F5',
        padding: '20px 18px',
        borderRadius: '8px',
      }}
    >
      <div style={{ fontSize: '22px', fontWeight: 'bold', marginRight: '10px' }}>{inventoryName}</div>
      <div style={{ color: 'rgba(0, 0, 0, 0.65)' }}>创建人：{creatorErp}</div>
    </Container>
  );
}

function StrategyItem({
  selected = true,
  inventoryName = '',
  inventoryId = '',
  creatorErp = '',
  onClick = (id: string) => {},
}) {
  const classNames = classnames('hp-strategy-card', { selected });

  return (
    <Card
      container={{
        padding: { vertical: true, horizontal: true },
        paddingSize: 'min',
        margin: false,
      }}
      onClick={() => onClick(inventoryId)}
      className={classNames}
      border={!selected}
      backgroundColor={!selected}
    >
      <div className="hp-strategy-card-title">{inventoryName}</div>
      <div className="hp-strategy-card-desc">创建人：{creatorErp}</div>
    </Card>
  );
}

// 货盘列表
function InventoryList({ list = [], checkedId, onSelect }: any) {
  if (list.length === 0) return null;
  if (list.length === 1)
    return (
      <div style={{ marginBottom: '10px' }}>
        <FullStrategyItem {...list[0]} />
      </div>
    );
  return (
    <div style={{ display: 'flex', marginBottom: '10px', overflowX: 'auto' }}>
      {list.map((item: any) => {
        return (
          <div key={item.inventoryId} style={{ marginRight: '8px' }}>
            <StrategyItem selected={checkedId === item.inventoryId} {...item} onClick={onSelect} />
          </div>
        );
      })}
    </div>
  );
}

type ResOfCategoryInventoryList = ReturnType<typeof apiCategoryInventoryList>;

function fetchInventoryInfo(query: {
  inventoryInfo: { inventoryId: string; inventoryName: string; creatorErp: string };
}) {
  return Promise.resolve(({
    ret: '0',
    data: {
      list: [query.inventoryInfo],
    },
    msg: 'success',
  } as unknown) as ResOfCategoryInventoryList);
}

// 用于区分是加在全部还是加载单个货盘
// 如果没有inventoryInfo则加载全部，
// 如果有inventoryInfo则构建函数返回结果
function apiAdvanceCategoryInventoryList(query: any): ResOfCategoryInventoryList {
  if (query.inventoryInfo) return fetchInventoryInfo(query);
  else return apiCategoryInventoryList({ ...(query || {}), type: 4 });
}

interface TypeOfChangeHistory {
  show: (query: TypeOfQuery) => void;
  hide: () => void;
}

interface TypeOfQuery {
  inventoryInfo?: { inventoryId: string; inventoryName: string; creatorErp: string };
  activityId: string;
}

// 货盘的修改历史，该组件有两种模式，
// 一种是所有货盘的修改历史
// 一种是单个货盘的修改历史
const ChangeHistory = React.forwardRef(function _ChanageHistory(props, ref: React.Ref<TypeOfChangeHistory>) {
  const [visible, setVisible] = useState(true);
  // 创建当前函数组件实例，向外部暴露方法
  useImperativeHandle<TypeOfChangeHistory, TypeOfChangeHistory>(ref, () => {
    return {
      show(query) {
        setQuery(query);
        setVisible(true);
      },
      hide() {
        setVisible(false);
      },
    };
  });
  // 当前选中的货盘id
  const [inventoryId, setInventoryId] = useState<string | null>(null);

  // 获取所有货盘列表，获取之后自动选中第一个
  const [query, setQuery] = useState<TypeOfQuery | null>(null);
  const onInventoryListLoadSuccess = useCallback((res: any) => {
    if (!res.data || !res.data.list.length) return;
    // 默认选中第一个货盘
    setInventoryId(res.data.list[0].inventoryId);
  }, []);

  const { data, loading, error, run } = useRequest<any>(apiAdvanceCategoryInventoryList, {
    manual: true,
    onSuccess: onInventoryListLoadSuccess,
  });

  // 开始加载所有变更的货盘列表
  useEffect(() => {
    if (!query) return;
    run(query);
  }, [query, run]);

  const loadStatus = loading ? LOAD_STATUS.ING : error ? LOAD_STATUS.FAILED : LOAD_STATUS.SUCCESS;

  const activityId = query && query.activityId;

  return (
    <AcoreConfigProvider>
      <Modal
        width="1152px"
        title="类目货盘规则更新记录"
        visible={visible}
        afterClose={() => {
          // 清除选中的InventoryId否则下次展示会先加载上一次选中货盘的变更记录
          setInventoryId(null);
        }}
        onOk={() => setVisible(false)}
        onCancel={() => setVisible(false)}
        okText="确认"
        cancelText="取消"
      >
        <Loadable LoadingTpl={JsLoading} FailedTpl={() => <StatusError showBtn={false} />} loadStatus={loadStatus}>
          <InventoryList onSelect={setInventoryId} checkedId={inventoryId} list={data?.list} />
          {!!inventoryId && !!activityId && (
            <InventoryUpdateHistory activityId={activityId} inventoryId={inventoryId} />
          )}
        </Loadable>
      </Modal>
    </AcoreConfigProvider>
  );
});

// 创建弹窗实例
function createChnageHistoryInstance() {
  let container: HTMLDivElement | null = null;
  let instance = React.createRef<TypeOfChangeHistory>();

  function mount() {
    container = document.createElement('div');
    document.body.appendChild(container);
    ReactDOM.render(<ChangeHistory ref={instance} />, container);
  }

  const changeHistory = {
    // 每一次show都会传入新的query，所以理论上都会重新刷新的
    show(query = ({} as unknown) as any) {
      if (!container) mount();
      instance.current?.show(query);
    },
    hide() {},
    unmount() {
      if (!container) return;
      ReactDOM.unmountComponentAtNode(container);
      container.parentNode?.removeChild(container);
    },
  };

  return changeHistory;
}

export default createChnageHistoryInstance();
